import { useEffect, useState } from "react";
import { firebase } from "../../../firebase";

interface UseUserRolesResult {
  roles: string[];
  loading: boolean;
}

/**
 * Reads roles from the current Firebase user's ID token claims.
 *
 * Returns `loading: true` until claims have been resolved, then `loading: false`
 * with the parsed role list. Lets callers tell "no roles yet" apart from
 * "claims loaded and the user has no admin roles".
 */
export const useUserRoles = (): UseUserRolesResult => {
  const [state, setState] = useState<UseUserRolesResult>({
    roles: [],
    loading: true,
  });

  useEffect(() => {
    const user = firebase.auth().currentUser;
    if (!user) {
      setState({ roles: [], loading: false });
      return;
    }

    user
      .getIdTokenResult()
      .then(result => {
        const roles: string[] = [];
        if (result.claims.role) roles.push(result.claims.role as string);
        if (result.claims.roles) roles.push(...(result.claims.roles as string[]));
        setState({ roles, loading: false });
      })
      .catch(() => {
        // Token fetch failure (expired credential, network blip) — treat as no roles
        // rather than stay loading forever. Server-side @Authorized still gates the data.
        setState({ roles: [], loading: false });
      });
  }, []);

  return state;
};
